import { UploadOutlined } from '@ant-design/icons';
import {
  App,
  AutoComplete,
  Button,
  Checkbox,
  Input,
  InputNumber,
  Modal,
  Radio,
  Slider,
  Space,
  Switch,
  Upload,
} from 'antd';
import {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { previewTemplate, saveTemplate, uploadTemplateImage } from '../api';
import {
  DEFAULT_CODE_STYLE,
  DEFAULT_NAME_BOX,
  DEFAULT_QR_BOX,
  GalaTemplate,
  NameBoxCfg,
  PREVIEW_NAME,
  QrBoxCfg,
  SAMPLE_SHORT_CODE,
  TemplateInput,
  errorText,
  groupFilter,
} from '../contract';
import {
  Box,
  CodeGeom,
  applyDrag,
  defaultCodeBox,
  pctToPx,
  qrToBox,
  withCodeFontSize,
} from '../lib/box-math';
import { fileUrl } from '../lib/file-url';
import styles from '../styles.module.scss';

/** Ảnh mẫu hiện vừa 480 px rộng; mọi toạ độ lưu theo % nên không phụ thuộc số này. */
const DISPLAY_W = 480;

type Target = 'name' | 'qr' | 'code';

/** Bước nhích bằng phím mũi tên (% ảnh); giữ Shift thì nhích xa hơn. */
const NUDGE_STEP = 0.1;
const NUDGE_STEP_SHIFT = 1;

const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

const ALIGN_FLEX = { left: 'flex-start', center: 'center', right: 'flex-end' } as const;

interface Drag {
  target: Target;
  mode: 'move' | 'resize';
  startX: number;
  startY: number;
  /** Khung lúc bắt đầu kéo (hộp chữ nhật %). */
  origin: { x: number; y: number; w: number; h: number };
}

interface Props {
  eventId: number;
  open: boolean;
  /** Có thì sửa mẫu này, không thì tạo mẫu mới. */
  template: GalaTemplate | null;
  /** Gợi ý nhóm khách cho ô "Mẫu mặc định cho nhóm". */
  groups: string[];
  onClose: () => void;
  /** Đã lưu (tạo hoặc sửa) — nơi gọi tải lại danh sách mẫu. */
  onSaved: () => void | Promise<void>;
}

const WEIGHTS = [
  { value: 400, label: 'Thường' },
  { value: 600, label: 'Vừa' },
  { value: 700, label: 'Đậm' },
  { value: 800, label: 'Rất đậm' },
];

/** Trình soạn mẫu vé: tải ảnh, kéo khung tên / QR, chỉnh kiểu chữ, xem thử (SPEC mục 4.1). */
const TemplateEditor = ({ eventId, open, template, groups, onClose, onSaved }: Props) => {
  const { message } = App.useApp();
  const [tplId, setTplId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [imageKey, setImageKey] = useState('');
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [nameBox, setNameBox] = useState<NameBoxCfg>(DEFAULT_NAME_BOX);
  const [qrEnabled, setQrEnabled] = useState(false);
  const [qr, setQr] = useState<QrBoxCfg>(DEFAULT_QR_BOX);
  /** Vị trí / cỡ mã chữ; null = chưa chỉnh tay, bám ngay dưới QR. */
  const [codeGeom, setCodeGeom] = useState<CodeGeom | null>(null);
  const [codeStyle, setCodeStyle] = useState(DEFAULT_CODE_STYLE);
  const [guestGroup, setGuestGroup] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [active, setActive] = useState<Target | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const dragRef = useRef<Drag | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Mở modal: nạp lại từ mẫu đang sửa (hoặc giá trị mặc định cho mẫu mới).
  useEffect(() => {
    if (!open) return;
    setTplId(template?.id ?? null);
    setName(template?.name ?? '');
    setImageKey(template?.imageKey ?? '');
    setSize(template ? { w: template.width, h: template.height } : null);
    setNameBox(template?.nameBox ?? DEFAULT_NAME_BOX);
    setQrEnabled(!!template?.qrBox);
    setQr(template?.qrBox ?? DEFAULT_QR_BOX);
    const sc = template?.shortCodeBox;
    setCodeGeom(sc ? { x: sc.x, y: sc.y, w: sc.w, h: sc.h, fontSize: sc.fontSize } : null);
    setCodeStyle(
      sc ? { color: sc.color, align: sc.align, weight: sc.weight ?? 700 } : DEFAULT_CODE_STYLE
    );
    setGuestGroup(template?.guestGroup ?? '');
    setIsDefault(template?.isDefault ?? false);
    setPreviewUrl(null);
    setActive(null);
  }, [open, template]);

  const scale = size ? DISPLAY_W / size.w : 1;

  /** Mã chữ đang dùng: đã chỉnh tay, hoặc mặc định ngay dưới QR. */
  const code: CodeGeom | null = codeGeom ?? (size ? defaultCodeBox(qr, size.w, size.h) : null);

  const boxOf = (target: Target): Box | null => {
    if (!size) return null;
    if (target === 'name') return nameBox;
    if (target === 'code') return code;
    return qrToBox(qr, size.w, size.h);
  };

  /** Bắt đầu chỉnh tay mã chữ: chốt vị trí đang bám QR thành vị trí riêng (dời QR sau đó không kéo mã theo). */
  const pinCode = (target: Target) => {
    if (target === 'code' && !codeGeom && code) setCodeGeom(code);
  };

  /** Ghi hộp % (sau khi kéo / nhích phím) vào đúng khung. */
  const placeBox = useCallback((target: Target, box: Box) => {
    if (target === 'qr') setQr((prev) => ({ ...prev, x: box.x, y: box.y, size: box.w }));
    else if (target === 'code')
      setCodeGeom((prev) => prev && { ...prev, x: box.x, y: box.y, w: box.w, h: box.h });
    else setNameBox((prev) => ({ ...prev, x: box.x, y: box.y, w: box.w, h: box.h }));
  }, []);

  const beginDrag = (e: ReactMouseEvent, target: Target, mode: Drag['mode']) => {
    const origin = boxOf(target);
    if (!origin) return;
    e.preventDefault();
    e.stopPropagation();
    // preventDefault chặn focus mặc định; focus khung ảnh để phím mũi tên nhích được khung vừa chọn.
    canvasRef.current?.focus({ preventScroll: true });
    pinCode(target);
    dragRef.current = {
      target,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin: { x: origin.x, y: origin.y, w: origin.w, h: origin.h },
    };
    setActive(target);
    setDragging(true);
  };

  // Đang kéo: nghe mousemove / mouseup trên window để kéo ra ngoài khung ảnh vẫn mượt, thả ở đâu cũng dừng.
  useEffect(() => {
    if (!dragging || !size) return undefined;
    const imgW = size.w;
    const imgH = size.h;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      // px màn hình -> % ảnh (ảnh hiện rộng DISPLAY_W, cao imgH * scale)
      const dxPct = ((e.clientX - d.startX) / (imgW * scale)) * 100;
      const dyPct = ((e.clientY - d.startY) / (imgH * scale)) * 100;
      placeBox(d.target, applyDrag(d.origin, d.mode, dxPct, dyPct, imgW, imgH, d.target === 'qr'));
    };
    const stop = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', stop);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', stop);
    };
  }, [dragging, size, scale, placeBox]);

  // Phím mũi tên: nhích khung đang chọn từng chút (Shift = xa hơn) để đặt chính xác.
  const onCanvasKeyDown = (e: ReactKeyboardEvent) => {
    const dir = ARROWS[e.key];
    if (!dir || !active || dragging || !size) return;
    if (active !== 'name' && !qrEnabled) return;
    const origin = boxOf(active);
    if (!origin) return;
    e.preventDefault();
    const step = e.shiftKey ? NUDGE_STEP_SHIFT : NUDGE_STEP;
    pinCode(active);
    placeBox(active, applyDrag(origin, 'move', dir[0] * step, dir[1] * step, size.w, size.h));
  };

  const patchName = (patch: Partial<NameBoxCfg>) => setNameBox((prev) => ({ ...prev, ...patch }));

  const onUpload = async (file: File) => {
    setUploading(true);
    try {
      const key = await uploadTemplateImage(file);
      setImageKey(key);
      setSize(null); // đo lại khi ảnh mới tải xong (onLoad)
      setPreviewUrl(null);
      if (!name.trim()) setName(file.name.replace(/\.[^.]+$/, ''));
    } catch (e) {
      message.error(errorText(e, 'Không tải được ảnh mẫu.'));
    } finally {
      setUploading(false);
    }
  };

  /** Lưu mẫu (tạo mới hoặc sửa). Trả mẫu đã lưu, hoặc null nếu thiếu dữ liệu / lỗi. */
  const persist = async (): Promise<GalaTemplate | null> => {
    if (!name.trim()) {
      message.warning('Hãy đặt tên cho mẫu vé.');
      return null;
    }
    if (!imageKey || !size) {
      message.warning('Hãy tải ảnh mẫu vé trước.');
      return null;
    }
    const input: TemplateInput = {
      name: name.trim(),
      imageKey,
      width: size.w,
      height: size.h,
      nameBox,
      qrBox: qrEnabled ? qr : null,
      shortCodeBox: qrEnabled && code ? { ...code, ...codeStyle } : null,
      guestGroup: guestGroup.trim() ? guestGroup.trim().toUpperCase() : null,
      isDefault,
    };
    const saved = await saveTemplate(eventId, input, tplId);
    setTplId(saved.id);
    await onSaved();
    return saved;
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const saved = await persist();
      if (saved) {
        message.success('Đã lưu mẫu vé.');
        onClose();
      }
    } catch (e) {
      message.error(errorText(e, 'Không lưu được mẫu vé.'));
    } finally {
      setSaving(false);
    }
  };

  // Xem thử cần mẫu đã có trên server: lưu trước (không đóng modal) rồi vẽ.
  const onPreview = async () => {
    setPreviewing(true);
    try {
      const saved = await persist();
      if (!saved) return;
      const res = await previewTemplate(eventId, saved.id, PREVIEW_NAME);
      setPreviewUrl(res.pngDataUrl);
    } catch (e) {
      message.error(errorText(e, 'Không vẽ thử được mẫu vé.'));
    } finally {
      setPreviewing(false);
    }
  };

  const boxStyle = (px: { left: number; top: number; width: number; height: number }): CSSProperties => ({
    left: px.left,
    top: px.top,
    width: px.width,
    height: px.height,
  });

  const nameBoxPx = size ? pctToPx(nameBox, size.w, size.h, scale) : null;
  const qrBoxPx = size ? pctToPx(qrToBox(qr, size.w, size.h), size.w, size.h, scale) : null;
  const codeBoxPx = size && code ? pctToPx(code, size.w, size.h, scale) : null;

  const sampleStyle: CSSProperties = {
    width: '100%',
    alignSelf: 'center',
    fontFamily: 'sans-serif',
    fontSize: (nameBox.fontSize / 100) * DISPLAY_W,
    fontWeight: nameBox.weight,
    color: nameBox.color,
    textAlign: nameBox.align,
    textTransform: nameBox.uppercase ? 'uppercase' : 'none',
    WebkitTextStroke: nameBox.stroke ? `${nameBox.stroke.width}px ${nameBox.stroke.color}` : undefined,
    whiteSpace: 'nowrap',
  };

  // Giống server: chữ không xuống dòng, to hơn khung thì tràn đều hai bên (căn giữa).
  const codeSampleStyle: CSSProperties = {
    flex: 'none',
    fontFamily: 'sans-serif',
    fontSize: ((code?.fontSize ?? 0) / 100) * DISPLAY_W,
    fontWeight: codeStyle.weight,
    color: codeStyle.color,
    letterSpacing: '0.12em',
    whiteSpace: 'nowrap',
  };

  return (
    <Modal
      title={tplId ? 'Sửa mẫu vé' : 'Thêm mẫu vé'}
      open={open}
      onCancel={onClose}
      width={1180}
      destroyOnClose
      maskClosable={false}
      footer={
        <Space>
          <Button onClick={onClose}>Đóng</Button>
          <Button onClick={onPreview} loading={previewing} disabled={!size || saving}>
            Xem thử
          </Button>
          <Button type="primary" onClick={onSave} loading={saving} disabled={previewing}>
            Lưu mẫu
          </Button>
        </Space>
      }
    >
      <div className={styles.tplEditor}>
        <div className={styles.tplCanvasWrap}>
          {imageKey ? (
            <div
              ref={canvasRef}
              className={styles.tplCanvas}
              style={{ width: DISPLAY_W }}
              tabIndex={0}
              onKeyDown={onCanvasKeyDown}
            >
              <img
                src={fileUrl(imageKey)}
                alt="Ảnh mẫu vé"
                draggable={false}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  if (img.naturalWidth > 0) {
                    setSize({ w: img.naturalWidth, h: img.naturalHeight });
                  }
                }}
              />
              {nameBoxPx && (
                <div
                  className={`${styles.tplBox} ${active === 'name' ? styles.tplBoxActive : ''}`}
                  style={boxStyle(nameBoxPx)}
                  onMouseDown={(e) => beginDrag(e, 'name', 'move')}
                  title="Kéo để di chuyển khung tên"
                >
                  <span style={sampleStyle}>{PREVIEW_NAME}</span>
                  <span
                    className={styles.tplHandle}
                    onMouseDown={(e) => beginDrag(e, 'name', 'resize')}
                    title="Kéo để đổi cỡ khung"
                  />
                </div>
              )}
              {qrEnabled && qrBoxPx && (
                <div
                  className={`${styles.tplBox} ${styles.tplBoxQr} ${
                    active === 'qr' ? styles.tplBoxActive : ''
                  }`}
                  style={boxStyle(qrBoxPx)}
                  onMouseDown={(e) => beginDrag(e, 'qr', 'move')}
                  title="Kéo để di chuyển khung QR"
                >
                  QR
                  <span
                    className={styles.tplHandle}
                    onMouseDown={(e) => beginDrag(e, 'qr', 'resize')}
                    title="Kéo để đổi cỡ khung QR"
                  />
                </div>
              )}
              {qrEnabled && codeBoxPx && (
                <div
                  className={`${styles.tplBox} ${styles.tplBoxCode} ${
                    active === 'code' ? styles.tplBoxActive : ''
                  }`}
                  style={{ ...boxStyle(codeBoxPx), justifyContent: ALIGN_FLEX[codeStyle.align] }}
                  onMouseDown={(e) => beginDrag(e, 'code', 'move')}
                  title="Kéo để di chuyển mã chữ"
                >
                  <span style={codeSampleStyle}>{SAMPLE_SHORT_CODE}</span>
                  <span
                    className={styles.tplHandle}
                    onMouseDown={(e) => beginDrag(e, 'code', 'resize')}
                    title="Kéo để đổi cỡ khung mã"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className={styles.tplEmpty}>Chưa có ảnh mẫu — bấm "Tải ảnh mẫu"</div>
          )}
          <p className={styles.hint}>
            {size ? `Ảnh gốc ${size.w} × ${size.h} px. ` : ''}
            Khung hồng là chỗ in tên (kéo để dời, kéo góc phải dưới để đổi cỡ), khung xanh là mã QR,
            khung cam là mã chữ. Bấm chọn một khung rồi dùng phím mũi tên để nhích từng chút (giữ
            Shift để nhích xa hơn).
          </p>
        </div>

        <div className={styles.tplPanel}>
          <div className={styles.tplRow}>
            <label htmlFor="tpl-name">Tên mẫu</label>
            <Input
              id="tpl-name"
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
              style={{ flex: 1, minWidth: 120 }}
              placeholder="VD: Vé VIP"
            />
          </div>
          <div className={styles.tplRow}>
            <label>Ảnh mẫu</label>
            <Upload
              accept="image/png,image/jpeg,image/webp"
              showUploadList={false}
              beforeUpload={(file) => {
                onUpload(file as File);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                {imageKey ? 'Đổi ảnh mẫu' : 'Tải ảnh mẫu'}
              </Button>
            </Upload>
          </div>
          <div className={styles.tplRow}>
            <label>Nhóm khách</label>
            <AutoComplete
              value={guestGroup}
              options={groups.map((g) => ({ value: g }))}
              filterOption={groupFilter(groups)}
              onChange={(v) => setGuestGroup(String(v ?? '').toUpperCase())}
              placeholder="Trống = không theo nhóm"
              style={{ flex: 1, minWidth: 120 }}
              allowClear
            />
          </div>
          <div className={styles.tplRow}>
            <label>Mặc định</label>
            <Switch checked={isDefault} onChange={setIsDefault} />
            <span className={styles.subtle}>Dùng khi vé không chọn mẫu</span>
          </div>

          <strong>Khung tên</strong>
          <div className={styles.tplRow}>
            <label>Cỡ chữ</label>
            <Slider
              min={2}
              max={12}
              step={0.1}
              value={nameBox.fontSize}
              onChange={(v) => patchName({ fontSize: v })}
              style={{ flex: 1, minWidth: 100 }}
              tooltip={{ formatter: (v) => `${v}% bề rộng` }}
            />
          </div>
          <div className={styles.tplRow}>
            <label htmlFor="tpl-color">Màu chữ</label>
            <input
              id="tpl-color"
              type="color"
              className={styles.colorInput}
              value={nameBox.color}
              onChange={(e) => patchName({ color: e.target.value })}
            />
          </div>
          <div className={styles.tplRow}>
            <label>Căn chữ</label>
            <Radio.Group
              value={nameBox.align}
              onChange={(e) => patchName({ align: e.target.value })}
              optionType="button"
              options={[
                { value: 'left', label: 'Trái' },
                { value: 'center', label: 'Giữa' },
                { value: 'right', label: 'Phải' },
              ]}
            />
          </div>
          <div className={styles.tplRow}>
            <label>Độ đậm</label>
            <Radio.Group
              value={nameBox.weight}
              onChange={(e) => patchName({ weight: e.target.value })}
              optionType="button"
              options={WEIGHTS}
            />
          </div>
          <div className={styles.tplRow}>
            <label>In hoa</label>
            <Switch checked={nameBox.uppercase} onChange={(v) => patchName({ uppercase: v })} />
          </div>
          <div className={styles.tplRow}>
            <label>Viền chữ</label>
            <Switch
              checked={!!nameBox.stroke}
              onChange={(v) => patchName({ stroke: v ? { color: '#000000', width: 3 } : null })}
            />
            {nameBox.stroke && (
              <>
                <input
                  type="color"
                  aria-label="Màu viền chữ"
                  className={styles.colorInput}
                  value={nameBox.stroke.color}
                  onChange={(e) =>
                    patchName({ stroke: { color: e.target.value, width: nameBox.stroke?.width ?? 3 } })
                  }
                />
                <InputNumber
                  min={1}
                  max={20}
                  value={nameBox.stroke.width}
                  onChange={(v) =>
                    patchName({ stroke: { color: nameBox.stroke?.color ?? '#000000', width: v ?? 3 } })
                  }
                  addonAfter="px"
                  style={{ width: 90 }}
                />
              </>
            )}
          </div>
          <div className={styles.tplRow}>
            <label>Số dòng</label>
            <InputNumber
              min={1}
              max={3}
              value={nameBox.maxLines}
              onChange={(v) => patchName({ maxLines: v ?? 1 })}
              style={{ width: 80 }}
            />
            <span className={styles.subtle}>Tên dài sẽ co chữ rồi xuống dòng</span>
          </div>
          <div className={styles.tplRow}>
            <label>Phông chữ</label>
            <span>Be Vietnam Pro</span>
          </div>

          <strong>Khung QR</strong>
          <div className={styles.tplRow}>
            <Checkbox
              className={styles.tplCheck}
              checked={qrEnabled}
              onChange={(e) => setQrEnabled(e.target.checked)}
            >
              Vẽ mã QR vào ảnh vé
            </Checkbox>
          </div>
          {qrEnabled && (
            <div className={styles.tplRow}>
              <label>Cỡ QR</label>
              <Slider
                min={5}
                max={40}
                step={0.5}
                value={qr.size}
                onChange={(v) =>
                  setQr((prev) => ({
                    ...prev,
                    size: Math.min(v, 100 - prev.x, size ? ((100 - prev.y) * size.h) / size.w : v),
                  }))
                }
                style={{ flex: 1, minWidth: 100 }}
                tooltip={{ formatter: (v) => `${v}% bề rộng` }}
              />
            </div>
          )}
          {qrEnabled && (
            <>
              <strong>Mã chữ dưới QR</strong>
              <div className={styles.tplRow}>
                <label>Cỡ chữ</label>
                <Slider
                  min={1}
                  max={10}
                  step={0.1}
                  value={code?.fontSize}
                  disabled={!code}
                  onChange={(v) =>
                    code && size && setCodeGeom(withCodeFontSize(code, v, size.w, size.h))
                  }
                  style={{ flex: 1, minWidth: 100 }}
                  tooltip={{ formatter: (v) => `${v}% bề rộng` }}
                />
              </div>
              <div className={styles.tplRow}>
                <label htmlFor="tpl-code-color">Màu chữ</label>
                <input
                  id="tpl-code-color"
                  type="color"
                  className={styles.colorInput}
                  value={codeStyle.color}
                  onChange={(e) => setCodeStyle((prev) => ({ ...prev, color: e.target.value }))}
                />
              </div>
              <div className={styles.tplRow}>
                <label>Độ đậm</label>
                <Radio.Group
                  value={codeStyle.weight}
                  onChange={(e) => setCodeStyle((prev) => ({ ...prev, weight: e.target.value }))}
                  optionType="button"
                  options={WEIGHTS}
                />
              </div>
              <div className={styles.tplRow}>
                <Button size="small" onClick={() => setCodeGeom(null)} disabled={!codeGeom}>
                  Đặt lại ngay dưới QR
                </Button>
                {!codeGeom && <span className={styles.subtle}>Đang bám theo QR</span>}
              </div>
            </>
          )}
        </div>

        <div className={styles.tplPreview}>
          <strong>Xem thử</strong>
          {previewUrl ? (
            <img src={previewUrl} alt={`Vé xem thử cho ${PREVIEW_NAME}`} />
          ) : (
            <p className={styles.hint}>
              Bấm "Xem thử" để vẽ vé với tên "{PREVIEW_NAME}" (mẫu sẽ được lưu trước).
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TemplateEditor;
